import { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api, type Challenge } from "../lib/api";
import { useTelegramBackButton } from "../hooks/useTelegramBackButton";

function Resolve() {
  const { challengeId } = useParams<{ challengeId: string }>();
  const navigate = useNavigate();
  const [challenge, setChallenge] = useState<Challenge | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [hasVoted, setHasVoted] = useState(false);
  const [userVote, setUserVote] = useState<string | null>(null);
  const [userSide, setUserSide] = useState<string | null>(null);
  const [userStake, setUserStake] = useState<number>(0);
  const [userWon, setUserWon] = useState<boolean | null>(null);
  const [userEarnings, setUserEarnings] = useState<number>(0);

  // Use Telegram's native back button (navigates back in history)
  useTelegramBackButton();

  const fetchChallenge = useCallback(async () => {
    if (!challengeId) return null;

    try {
      const response = await api.getChallenge(challengeId);
      setChallenge(response.challenge);
      setUserVote(response.userVote);
      setUserSide(response.userSide);
      setUserStake(response.userStake);
      setUserWon(response.userWon);
      setUserEarnings(response.userEarnings);

      if (response.userVote) {
        setHasVoted(true);
      }

      return response.challenge;
    } catch (err) {
      console.error("Failed to fetch challenge:", err);
      setError("Failed to load challenge");
      return null;
    }
  }, [challengeId]);

  // Initial load
  useEffect(() => {
    fetchChallenge().finally(() => setIsLoading(false));
  }, [fetchChallenge]);

  // Poll for updates when waiting for other votes
  useEffect(() => {
    if (!hasVoted || !challenge || challenge.status !== "RESOLVING") return;

    const pollInterval = setInterval(async () => {
      const updated = await fetchChallenge();
      if (updated && updated.status !== "RESOLVING") {
        clearInterval(pollInterval);
      }
    }, 2000); // Poll every 2 seconds

    return () => clearInterval(pollInterval);
  }, [hasVoted, challenge?.status, fetchChallenge]);

  const handleVote = async (outcome: "YES" | "NO") => {
    if (!challengeId || isSubmitting) return;

    setIsSubmitting(true);
    setError(null);
    try {
      await api.vote(challengeId, outcome);
      setHasVoted(true);
      setUserVote(outcome);
      // Refetch to get updated status
      await fetchChallenge();
    } catch (err) {
      console.error("Failed to submit vote:", err);
      setError(err instanceof Error ? err.message : "Failed to submit vote");
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isLoading) {
    return <div className="page loading">Loading...</div>;
  }

  if (error && !challenge) {
    return (
      <div className="page error-state">
        <p>{error}</p>
        <button className="btn primary" onClick={() => navigate("/")}>
          Go Home
        </button>
      </div>
    );
  }

  if (!challenge) {
    return (
      <div className="page error-state">
        <p>Challenge not found</p>
        <button className="btn primary" onClick={() => navigate("/")}>
          Go Home
        </button>
      </div>
    );
  }

  // Helper to get label for a side
  const getSideLabel = (side: string) => {
    if (!challenge) return side;
    return side === "YES" ? (challenge.labelA || "Yes") : (challenge.labelB || "No");
  };

  // SETTLED - Winner screen
  if (challenge.status === "SETTLED" && userWon === true) {
    const profit = userEarnings - userStake;
    return (
      <div className="page resolve outcome-screen winner">
        <div className="outcome-icon">🎉</div>
        <h1>You Won!</h1>

        <div className="challenge-card">
          <p className="description">"{challenge.description}"</p>
          <p className="outcome-label">
            Outcome: <strong>{challenge.outcome ? getSideLabel(challenge.outcome) : ""}</strong> was correct
          </p>
        </div>

        <div className="earnings-card">
          <div className="earnings-row">
            <span>Your stake</span>
            <span>{userStake.toFixed(2)} TON</span>
          </div>
          <div className="earnings-row">
            <span>Opponent's stake</span>
            <span>+{profit.toFixed(2)} TON</span>
          </div>
          <div className="earnings-row total">
            <span>Total earned</span>
            <span className="amount">{userEarnings.toFixed(2)} TON</span>
          </div>
        </div>

        <p className="balance-credited">Balance has been credited to your account</p>

        <button className="btn primary full-width" onClick={() => navigate("/")}>
          Go Home
        </button>
      </div>
    );
  }

  // SETTLED - Loser screen
  if (challenge.status === "SETTLED" && userWon === false) {
    return (
      <div className="page resolve outcome-screen loser">
        <div className="outcome-icon">😔</div>
        <h1>Better Luck Next Time</h1>

        <div className="challenge-card">
          <p className="description">"{challenge.description}"</p>
          <p className="outcome-label">
            Outcome: <strong>{challenge.outcome ? getSideLabel(challenge.outcome) : ""}</strong> was correct
          </p>
        </div>

        <div className="loss-info">
          <p>You picked <strong>{userSide ? getSideLabel(userSide) : ""}</strong>, but the outcome was <strong>{challenge.outcome ? getSideLabel(challenge.outcome) : ""}</strong></p>
          <p className="stake-lost">Stake lost: {userStake.toFixed(2)} TON</p>
        </div>

        <button className="btn primary full-width" onClick={() => navigate("/")}>
          Go Home
        </button>
      </div>
    );
  }

  // REFUNDED - Dispute screen
  if (challenge.status === "REFUNDED") {
    return (
      <div className="page resolve outcome-screen refunded">
        <div className="outcome-icon">⚖️</div>
        <h1>No Agreement Reached</h1>

        <div className="challenge-card">
          <p className="description">"{challenge.description}"</p>
        </div>

        <div className="dispute-info">
          <p>You and your opponent couldn't agree on the outcome.</p>
          <div className="votes-comparison">
            <span>You voted: <strong>{userVote}</strong></span>
          </div>
          <p className="refund-note">Since there was no agreement, all stakes have been refunded.</p>
        </div>

        <div className="refund-card">
          <span>Refunded to your account</span>
          <span className="amount">{userStake.toFixed(2)} TON</span>
        </div>

        <button className="btn primary full-width" onClick={() => navigate("/")}>
          Go Home
        </button>
      </div>
    );
  }

  // RESOLVING - Voting in progress
  if (challenge.status === "RESOLVING") {
    return (
      <div className="page resolve">
        <h1>Resolve Challenge</h1>

        {error && <div className="error-banner">{error}</div>}

        <div className="challenge-card">
          <p className="description">"{challenge.description}"</p>
          <div className="pool-info">
            <span>Total Pool: {challenge.poolTotal} TON</span>
          </div>
          <div className="sides-info">
            <div className="side-info yes">
              <span className="label">{challenge.labelA || "YES"}</span>
              <span className="total">{challenge.yesSide.total.toFixed(2)} TON</span>
            </div>
            <div className="side-info no">
              <span className="label">{challenge.labelB || "NO"}</span>
              <span className="total">{challenge.noSide.total.toFixed(2)} TON</span>
            </div>
          </div>
        </div>

        {hasVoted ? (
          <div className="waiting-status">
            <div className="spinner"></div>
            <h2>Vote Submitted</h2>
            <p>You voted: <strong>{userVote ? getSideLabel(userVote) : ""}</strong></p>
            <p className="waiting-text">Waiting for the other party to vote...</p>
            <p className="hint">This page will update automatically</p>
          </div>
        ) : (
          <div className="voting-section">
            <h2>What was the outcome?</h2>
            <p className="your-side">You picked: <strong>{userSide ? getSideLabel(userSide) : ""}</strong></p>
            <div className="vote-buttons">
              <button
                className="btn vote-btn yes"
                onClick={() => handleVote("YES")}
                disabled={isSubmitting}
              >
                {challenge.labelA || "YES"} was correct
              </button>
              <button
                className="btn vote-btn no"
                onClick={() => handleVote("NO")}
                disabled={isSubmitting}
              >
                {challenge.labelB || "NO"} was correct
              </button>
            </div>
            <p className="vote-hint">Be honest! If votes don't match, stakes will be refunded.</p>
          </div>
        )}
      </div>
    );
  }

  // LOCKED - Check if timer allows resolution
  const hasTimer = !!challenge.timerEndsAt;
  const timerEndsAt = challenge.timerEndsAt ? new Date(challenge.timerEndsAt) : null;
  const now = new Date();
  const timerExpired = timerEndsAt ? timerEndsAt <= now : true;
  const canResolve = !hasTimer || timerExpired;

  // Format time remaining for timer
  const getTimeRemaining = () => {
    if (!timerEndsAt) return null;
    const diff = timerEndsAt.getTime() - now.getTime();
    if (diff <= 0) return null;

    const hours = Math.floor(diff / (1000 * 60 * 60));
    const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
    const seconds = Math.floor((diff % (1000 * 60)) / 1000);

    if (hours > 0) return `${hours}h ${minutes}m`;
    if (minutes > 0) return `${minutes}m ${seconds}s`;
    return `${seconds}s`;
  };

  return (
    <div className="page resolve">
      <h1>Resolve Challenge</h1>

      {error && <div className="error-banner">{error}</div>}

      <div className="challenge-card">
        <p className="description">"{challenge.description}"</p>
        <div className="pool-info">
          <span>Total Pool: {challenge.poolTotal} TON</span>
        </div>
        <div className="sides-info">
          <div className="side-info yes">
            <span className="label">{challenge.labelA || "YES"}</span>
            <span className="total">{challenge.yesSide.total.toFixed(2)} TON</span>
          </div>
          <div className="side-info no">
            <span className="label">{challenge.labelB || "NO"}</span>
            <span className="total">{challenge.noSide.total.toFixed(2)} TON</span>
          </div>
        </div>
      </div>

      {canResolve ? (
        <div className="start-resolution">
          <h2>Ready to Resolve</h2>
          <p className="resolve-info">
            Both sides have joined. Start the resolution to vote on the outcome.
          </p>
          <button
            className="btn primary full-width"
            onClick={async () => {
              setIsSubmitting(true);
              setError(null);
              try {
                await api.startResolution(challengeId!);
                await fetchChallenge();
              } catch (err) {
                console.error("Start resolution error:", err);
                setError(err instanceof Error ? err.message : "Failed to start resolution");
              } finally {
                setIsSubmitting(false);
              }
            }}
            disabled={isSubmitting}
          >
            {isSubmitting ? "Starting..." : "Start Resolution"}
          </button>
        </div>
      ) : (
        <div className="waiting-for-timer">
          <h2>Waiting for Timer</h2>
          <p className="timer-info">
            Both sides have joined. Resolution will be available when the timer expires.
          </p>
          <div className="countdown-display">
            <span className="countdown-icon">⏱️</span>
            <span className="countdown-time">{getTimeRemaining()}</span>
          </div>
          <p className="timer-hint">Come back when the time is up to resolve the challenge.</p>
          <button className="btn secondary full-width" onClick={() => navigate("/")}>
            Go Home
          </button>
        </div>
      )}
    </div>
  );
}

export default Resolve;
